/**
 * Enrich a simulation topology (DiagramSpec) with live runtime data before
 * rendering: metric snapshots + history for metric nodes, and value/handler
 * wiring for `simInput` slider nodes.
 *
 * Shared by the workspace diagram pane and the embed player so both render
 * paths stay behaviorally identical (controlled sliders, metric charts,
 * inline metric values). The node-enrichment logic itself lives in the pure,
 * unit-tested `enrichSpec` (components/enrich-spec.ts); this hook only owns
 * the React memoization and derives the input-descriptor lookup map.
 */
import { useMemo } from "react";
import type { DiagramSpec } from "@diagram/diagram-view";
import type { InputDescriptor } from "@diagram/sim-model";
import type { TaggedMetricSnapshot } from "@diagram/sim-worker";
import type { MetricTimeSeries } from "./useMetricHistory";
import { enrichSpec } from "../components/enrich-spec";

/** The live-simulation state the enrichment reads (subset of useSimulation). */
interface EnrichmentSources {
  topology: DiagramSpec | null;
  metricsByNode: Map<string, TaggedMetricSnapshot[]>;
  inputDescriptors: InputDescriptor[];
  inputValues: Record<string, number | boolean>;
  setInputValue: (id: string, value: number | boolean) => void;
  /** Per-node metric time series from useMetricHistory. */
  metricHistory: Map<string, MetricTimeSeries[]>;
  /** Chart time window in seconds, or null for "all". */
  timeWindow: number | null;
}

/**
 * Memoized enrichment of the topology with metrics, history, and input
 * wiring. Returns null while there is no topology. Spec-level fields
 * (edges, groups, appearance) pass through `enrichSpec` untouched.
 */
export function useEnrichedSpec({
  topology,
  metricsByNode,
  inputDescriptors,
  inputValues,
  setInputValue,
  metricHistory,
  timeWindow,
}: EnrichmentSources): DiagramSpec | null {
  const inputDescriptorMap = useMemo(() => {
    const map = new Map<string, InputDescriptor>();
    for (const d of inputDescriptors) {
      map.set(d.id, d);
    }
    return map;
  }, [inputDescriptors]);

  return useMemo((): DiagramSpec | null => {
    if (!topology) return null;
    return enrichSpec(topology, {
      metricsByNode,
      metricHistory,
      timeWindow,
      inputDescriptorMap,
      inputValues,
      setInputValue,
    });
  }, [
    topology,
    metricsByNode,
    inputDescriptorMap,
    inputValues,
    setInputValue,
    metricHistory,
    timeWindow,
  ]);
}
